import React from 'react';
import throttle from 'lodash.throttle';
import { Grid, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';

import { Spacing, TextInput } from 'components';
import { chackAvailability } from 'graphql/utils';
import { IS_SHOW_AVAILABLE } from 'graphql/show';
import useGlobalStyles from 'hooks/useGlobalStyles';
import { searchItunes } from 'services/itunes';
import { ItunesShow } from 'types/itunes';

import useStyles from './styles';

interface Props {
  onChange: (selectedShowId?: string, selectedShowFeed?: string) => void;
}

const ItunesSearch: React.FC<Props> = (props) => {
  const { onChange } = props;
  const classes = useStyles();
  const globalClasses = useGlobalStyles();

  // Local State
  const messages = {
    unavalilable: 'Another user has already claimed this show',
    missing: 'Select a show from the Apple Podcast Directory',
  };
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedShow, setSelectedShow] = React.useState({} as ItunesShow);
  const [searchResults, setSearchResults] = React.useState([]);
  const [isShowAvailable, setIsShowAvailable] = React.useState(false);
  const [isShowTouched, setIsShowTouched] = React.useState(false);

  const handleAutocompleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getShowInputError = () => {
    return selectedShow.collectionId
      ? !isShowAvailable && messages.unavalilable
      : isShowTouched && messages.missing;
  };

  const search = React.useMemo(
    () =>
      throttle((input: string, callback: (results?: any) => void) => {
        searchItunes(input).then((results) => callback(results));
      }, 200),
    [],
  );

  const checkShowAvailability = async () => {
    const { collectionId, feedUrl } = selectedShow;
    const data = await chackAvailability(IS_SHOW_AVAILABLE, String(collectionId || ''));
    setIsShowAvailable(data.isShowAvailable);
    const id = collectionId && String(collectionId);
    onChange(id, feedUrl);
  };

  // Selected Show Changed
  React.useEffect(() => {
    checkShowAvailability();
    // eslint-disable-next-line
  }, [selectedShow]);

  // Search Value Changed
  React.useEffect(() => {
    let active = true;
    if (!searchValue || searchValue === '') {
      setSearchResults([]);
      return undefined;
    }

    search(searchValue, (results?: any) => {
      if (active) {
        setSearchResults(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [searchValue, search]);

  const renderOption = (option: ItunesShow) => (
    <Grid container alignItems="center" spacing={3}>
      <Grid item>
        <img
          className={globalClasses.thumbnail}
          src={option.artworkUrl100}
          alt={`${option.collectionName} art`}
        />
      </Grid>
      <Grid item xs>
        <Typography className={classes.resultTitle} variant="body2">
          {option.collectionName}
        </Typography>
        <Spacing height="8px" />
        <Typography variant="subtitle2">{option.artistName}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Autocomplete
      id="itunes-search"
      getOptionLabel={(option: ItunesShow) => option.collectionName || option.artistName || ''}
      getOptionSelected={(option: ItunesShow, value: ItunesShow) =>
        option && option.collectionId === value.collectionId
      }
      value={selectedShow}
      filterOptions={(x: any) => x}
      noOptionsText="No shows found"
      options={searchResults}
      autoComplete
      includeInputInList
      onChange={(e: any, value: any) => {
        setSelectedShow(value || {});
      }}
      renderInput={(params: any) => (
        <TextInput
          {...params}
          title="Podcast"
          placeholder="Search for your show"
          onChange={handleAutocompleteChange}
          helperText=""
          errorMessage={getShowInputError()}
          endAdornment={<SearchIcon />}
          onBlur={() => {
            setIsShowTouched(true);
          }}
        />
      )}
      renderOption={renderOption}
    />
  );
};

export default ItunesSearch;
